import { useToast } from '@chakra-ui/react'
import {
  useCPULoad,
  useDailyEvent,
  useLocalSessionId,
  useNetwork,
  useParticipantProperty
} from '@daily-co/daily-react'
import { datadogLogs } from '@datadog/browser-logs'
import { trackEvent } from '@lib/clinician-tracking'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'

import { LayoutSettingsModal } from '../components/layout-settings-modal'
import { useRecordingDuration } from '../hooks/use-recording-time'

type ActiveTelehealthSessionContextType = {
  activeSidebarTab: SideBarTab
  toggleSidebar: (tab: SideBarTab) => void
  isSidebarVisible: boolean
  isBackgroundBlurred: boolean
  toggleBackgroundBlur: () => void
  isCallOwner: boolean
  currentLayout: LayoutOption
  setLayout: (option: Partial<LayoutOption>) => void
  setIsLayoutSettingsModalOpen: (open: boolean) => void
  logger: (message: string, data?: any) => void
  recordingStartedAt: string | null
  setRecordingStartedAt: (startedAt: string | null) => void
}

type SideBarTab = 'assist' | 'participants' | 'none'
type LayoutOptionName = 'spotlight' | 'gallery'
type LayoutOption = {
  isUserSet: boolean
  name: LayoutOptionName
}

export const ActiveTelehealthSessionContext = createContext<
  ActiveTelehealthSessionContextType | undefined
>(undefined)

export const ActiveTelehealthSessionProvider = ({
  children,
  defaultSidebarTab = 'none',
  isBackgroundBlurred = false,
  toggleBackgroundBlur = () => {},
  isCallOwner,
  sessionId,
  clinicianDisplayId
}: {
  children: React.ReactNode
  defaultSidebarTab?: SideBarTab
  isBackgroundBlurred: boolean
  toggleBackgroundBlur: () => void
  isCallOwner: boolean
  sessionId?: string
  clinicianDisplayId?: string
}) => {
  const dailyLocalSessionId = useLocalSessionId()
  const dailyUserName = useParticipantProperty(dailyLocalSessionId, 'user_name')

  const [isSidebarVisible, setIsSidebarVisible] = useState(
    defaultSidebarTab !== 'none'
  )
  const [activeSidebarTab, setActiveSidebarTab] =
    useState<SideBarTab>(defaultSidebarTab)
  const [currentLayout, _setLayout] = useState<LayoutOption>({
    isUserSet: false,
    name: 'spotlight'
  })
  const [isLayoutSettingsModalOpen, setIsLayoutSettingsModalOpen] =
    useState(false)
  const [recordingStartedAt, setRecordingStartedAt] = useState<string | null>(
    null
  )

  const logger = useCallback(
    (message: string, data: any = {}) => {
      datadogLogs.logger.info(`[Telehealth-Session] ${message}`, {
        ...data,
        dailyLocalSessionId,
        dailyUserName,
        sessionId,
        clinicianDisplayId
      })
    },
    [dailyLocalSessionId, sessionId, clinicianDisplayId, dailyUserName]
  )

  const toggleSidebar = (tab: SideBarTab) => {
    if (tab === activeSidebarTab || tab === 'none') {
      setActiveSidebarTab('none')
      setIsSidebarVisible(false)
    } else {
      setActiveSidebarTab(tab as 'assist' | 'participants')
      setIsSidebarVisible(true)
    }
  }

  const setLayout = (option: Partial<LayoutOption>) => {
    _setLayout((prev: LayoutOption) => ({
      ...prev,
      ...option
    }))
  }

  return (
    <ActiveTelehealthSessionContext.Provider
      value={{
        activeSidebarTab,
        toggleSidebar,
        isSidebarVisible,
        isBackgroundBlurred,
        toggleBackgroundBlur,
        isCallOwner,
        currentLayout,
        setLayout,
        setIsLayoutSettingsModalOpen,
        logger,
        recordingStartedAt,
        setRecordingStartedAt
      }}
    >
      {/* @ts-ignore */}
      <TelehealthRecordingTimerProvider>
        {children}
        <LayoutSettingsModal
          isOpen={isLayoutSettingsModalOpen}
          onClose={() => setIsLayoutSettingsModalOpen(false)}
        />
        <TelehealthPerformanceTracking logger={logger} />
      </TelehealthRecordingTimerProvider>
    </ActiveTelehealthSessionContext.Provider>
  )
}

const TelehealthPerformanceTracking = ({
  logger
}: {
  logger: (message: string, data?: any) => void
}) => {
  const [consecutiveLowNetworkEvents, setConsecutiveLowNetworkEvents] =
    useState(0)
  const lastToastTime = useRef<number>(0)
  const TOAST_COOLDOWN = 30000 // 30 seconds between toasts
  const LOW_NETWORK_THRESHOLD = 3 // Show toast after 3 consecutive low network events

  const toast = useToast()

  const { threshold } = useNetwork()
  const { state: cpuState } = useCPULoad()

  useDailyEvent('nonfatal-error', event => {
    logger('daily-event.nonfatal-error', {
      event
    })
  })

  useDailyEvent('transcription-error', event => {
    logger('daily-event.transcription-error', {
      event
    })
  })

  useDailyEvent('error', event => {
    if (event.error?.type !== 'ejected') {
      logger('daily-event.error', {
        event
      })
    }
  })

  useDailyEvent('local-screen-share-started', () => {
    trackEvent('event:telehealth-session.screen-share-started')
  })

  useDailyEvent('local-screen-share-stopped', () => {
    trackEvent('event:telehealth-session.screen-share-stopped')
  })

  useEffect(() => {
    if (threshold === 'low') {
      logger('participant.low_network_quality')
      setConsecutiveLowNetworkEvents((prev: number) => prev + 1)

      const now = Date.now()
      if (
        consecutiveLowNetworkEvents >= LOW_NETWORK_THRESHOLD &&
        now - lastToastTime.current > TOAST_COOLDOWN
      ) {
        lastToastTime.current = now
        toast({
          status: 'warning',
          title: 'Low network quality',
          description:
            'Please check your internet connection or move closer to your router.',
          duration: 10000,
          isClosable: true,
          position: 'top-right'
        })
      }
    } else {
      setConsecutiveLowNetworkEvents(0)
    }
  }, [threshold])

  useEffect(() => {
    if (cpuState === 'high') {
      logger('participant.high_cpu_usage')
    }
  }, [cpuState])

  return null
}

const TelehealthRecordingTimerContext = createContext<
  ReturnType<typeof useRecordingDuration> | undefined
>(undefined)

export const TelehealthRecordingTimerProvider = ({
  children
}: {
  children: React.ReactNode
}) => {
  const { recordingStartedAt, isCallOwner } = useActiveTelehealthSession()

  const recordingDuration = useRecordingDuration({
    recordingStartedAt
  })

  useEffect(() => {
    if (isCallOwner && recordingStartedAt) {
      recordingDuration.beginRecordingTimer()
    }
  }, [isCallOwner, recordingDuration])

  return (
    <TelehealthRecordingTimerContext.Provider value={recordingDuration}>
      {children}
    </TelehealthRecordingTimerContext.Provider>
  )
}

export const useActiveTelehealthSession = () => {
  const context = useContext(ActiveTelehealthSessionContext)
  if (!context) {
    throw new Error(
      'useActiveTelehealthSession must be used within a ActiveTelehealthSessionProvider'
    )
  }
  return context
}

export const useTelehealthRecordingTimer = () => {
  const context = useContext(TelehealthRecordingTimerContext)
  if (!context) {
    throw new Error(
      'useTelehealthRecordingTimer must be used within a TelehealthRecordingTimerProvider'
    )
  }
  return context
}
